import DataGrid from 'devextreme-react/data-grid';
import dxDataGrid, { RowClickEvent } from 'devextreme/ui/data_grid';

import { TRowData, TData, TId } from 'types/rowDefs';
import { TOnRowReorder } from 'types/gridDefs';

/* -------------------- */

// when editing an existing row the getRowIndexByKey will immediately return a zero-based result
// after adding a new row, then trying to edit it, there may be a delay before the datagrid interal data array is ready - so retry the function with a delay
const maxRetries = 5;
const delay = 50;
const getRowIndex = async (dataGrid: dxDataGrid, rowId: TId) => {
    let rowIndex = dataGrid.getRowIndexByKey(rowId);
    let retries = maxRetries;
    const pause = new Promise((resolve) => globalThis.setTimeout(resolve, delay));

    while (rowIndex === -1 && retries > 0) {
        await pause;
        rowIndex = dataGrid.getRowIndexByKey(rowId);
    }
    return rowIndex;
};

export const setRowEditMode = async (refDataGrid: DataGrid<unknown, unknown> | null, editSelectedRow: boolean | undefined, selectedRowId: TId | undefined) => {
    if (refDataGrid?.instance) {
        if (editSelectedRow && selectedRowId) {
            const rowIndex = await getRowIndex(refDataGrid.instance, selectedRowId);
            refDataGrid.instance.editRow(rowIndex);
        } else {
            refDataGrid.instance.cancelEditData();
        }
    }
};

/* -------------------- */

const dxSelectedRowKeys = 'selectedRowKeys';
const dxEditingEditRowKey = 'editing.editRowKey';
const dxEditingAllowUpdating = 'editing.allowUpdating';

export const selectRow = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    (rowId: TId | undefined) => {
        if (dataGrid) {
            const selectedRowKeys = dataGrid.getSelectedRowKeys();

            if (rowId && !selectedRowKeys.includes(rowId)) {
                dataGrid.option(dxSelectedRowKeys, [rowId]);
            } else {
                dataGrid.option(dxSelectedRowKeys, []);
            }
        }
    };

export const getSelectedRowData = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    (): TRowData | null => {
        if (dataGrid) {
            const selectedRowsData = dataGrid.getSelectedRowsData() as TRowData[];
            return selectedRowsData?.length ? selectedRowsData[0] : null;
        }
        return null;
    };

export const editRow = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    (rowId: TId | undefined) => {
        if (dataGrid) {
            const currentEditRowKey = dataGrid.option(dxEditingEditRowKey) as TId;

            if (rowId && rowId !== currentEditRowKey) {
                // starting to edit a row
                dataGrid.option(dxSelectedRowKeys, [rowId]);        // select the row (and ergo de-select any other selected rows)
                dataGrid.option(dxEditingEditRowKey, rowId);        // set the row into edit mode
            } else {
                // stop editing the current edit row
                dataGrid.resetOption(dxSelectedRowKeys);            // de-select the row
                dataGrid.resetOption(dxEditingEditRowKey);          // set the row out of edit mode
                dataGrid.option(dxEditingAllowUpdating, false);     // switch off editing (to force closure of all input elements)
                dataGrid.option(dxEditingAllowUpdating, true);      // switch editing back on
            }
        }
    };

/* -------------------- */

export const handleRowSelect = (onRowSelect: ((data: TRowData) => void) | undefined) =>
    (evt: RowClickEvent<unknown, unknown>) => {
        if (onRowSelect && evt.data && (evt.data as TRowData).allowSelect) {
            onRowSelect(evt.data as TRowData);
        }
    };

type TRowReorderEvent = {
    readonly component: dxDataGrid<unknown, unknown>;
    readonly itemData?: TRowData;
    readonly fromIndex: number;
    readonly toIndex: number;
}
export const handleRowReorder = (rows: TData, onRowReorder: TOnRowReorder) =>
    (evt: TRowReorderEvent) => {
        const dataGrid = evt.component;
        if (dataGrid && rows && evt.itemData) {
            const visibleRows = dataGrid.getVisibleRows();
            const fromId = (visibleRows[evt.fromIndex].data as TRowData).id;
            const fromIndex = rows.findIndex(row => row.id === fromId);
            const toId = (visibleRows[evt.toIndex].data as TRowData).id;
            const toIndex = rows.findIndex(row => row.id === toId);

            if (fromIndex < toIndex) {
                rows.splice(toIndex, 0, evt.itemData);
                rows.splice(fromIndex, 1);
            } else {
                rows.splice(fromIndex, 1);
                rows.splice(toIndex, 0, evt.itemData);
            }
            dataGrid.refresh();

            onRowReorder(fromIndex, toIndex, rows);
        }
    };

/* -------------------- */
